import { forwardRef, useId } from 'react';
import { cn } from 'lib/cn';
import { Field } from './Field';
import { ChevronDownIcon } from 'components/icons';

/**
 * Labelled native select.
 *
 * @param {Array<{ value: string, label: string, disabled?: boolean }>} options
 * @param {string} [placeholder] renders a disabled first option
 */
export const Select = forwardRef(function Select(
  {
    label,
    error,
    hint,
    required,
    id,
    options = [],
    placeholder,
    className,
    containerClassName,
    ...props
  },
  ref,
) {
  const autoId = useId();
  const selectId = id || autoId;

  return (
    <Field
      label={label}
      htmlFor={selectId}
      error={error}
      hint={hint}
      required={required}
      className={containerClassName}
    >
      <div className="relative">
        <select
          id={selectId}
          ref={ref}
          className={cn(
            'h-10 w-full appearance-none rounded-lg border bg-white pl-3 pr-9 text-sm text-slate-900 transition focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:bg-surface-50',
            error ? 'border-red-400' : 'border-surface-300',
            className,
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
    </Field>
  );
});

export default Select;
